import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { clearAuthStorage } from '@/utils/authStorage';
import { authService, authApi } from '@/services/authService';
import { useNavigate } from 'react-router-dom';
import { useVideoFeaturesStore } from '@/store/videoFeatures/store';

interface Address {
  line1: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

interface DateOfBirth {
  day: string | number;
  month: string | number;
  year: string | number;
}

interface User {
  id: string;
  id_user: string;
  nome: string;
  sobrenome?: string;
  email: string;
  avatar?: string;
  telefone?: string;
  nivel: 'admin' | 'seller' | 'manager' | 'user' | 'support' | 'customer' | 'platform' | 'member';
  status: string;
  cpf: string;

  // Campos de membro (nivel === 'member')
  permissions?: string[];
  access_type?: 'ADMIN' | 'MEMBER';
  folder_access?: { folder_id: string; level: 'view' | 'edit' }[];
  member_owner_id?: string;

  // Novos campos vindos do MongoDB / Connect
  id_stripe_account?: string;
  id_Stripe_Client?: string;
  business_url?: string;

  // Objetos aninhados
  address: Address;
  dob: DateOfBirth;

  // Clicks e Plano
  purchased_plan?: string;
  clicks_per_plan?: number;
  excess_clicks?: number;
  clicks_per_test?: number;

  [key: string]: any;
}

interface Plan {
  nome: string;
  clicks_total: number;
  clicks_restantes: number;
  clicks_usados: number;
  excess_clicks: number;
  periodo_teste: boolean;
  descricao: string;
  status: string;
  dias_renovacao: number;
  data_renovacao: string | null;
}

interface AuthContextType {
  user: User | null;
  plan: Plan | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: User, token: string, plan?: Plan, keepConnected?: boolean) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasPermission: (featureKey: string, level?: 'view' | 'edit') => boolean;
  isMember: boolean;
  isAdminMember: boolean;
}

const CustomAuthContext = createContext<AuthContextType | null>(null);

interface CustomAuthProviderProps {
  children: ReactNode;
}

export const CustomAuthProvider: React.FC<CustomAuthProviderProps> = ({ children }) => {
  useLanguage();
  const [user, setUser] = useState<User | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Busca dados atualizados do usuário via GET /me
   * Não confia em dados do token, sempre busca do backend
   */
  const refreshUser = async () => {
    try {
      const response = await authService.getMe();
      if (response.data?.success && response.data?.user) {
        setUser(response.data.user);

        // Atualiza cache
        const loginChecked = localStorage.getItem('checkedLogin');
        const storage = loginChecked ? localStorage : sessionStorage;
        storage.setItem('user', JSON.stringify(response.data.user));
        // Regrava o userId: só era salvo no login(), some ao reidratar em nova aba
        storage.setItem('userId', response.data.user.id || response.data.user.id_user);

        // Sempre sincroniza o plano com o backend, inclusive quando vier null
        // (ex.: subscription cancelada). Antes, o cache antigo persistia.
        if (response.data.plano) {
          setPlan(response.data.plano);
          storage.setItem('plano', JSON.stringify(response.data.plano));
        } else {
          setPlan(null);
          storage.removeItem('plano');
        }
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  // Detecta limpeza de cache/storage em tempo real (sem reload da página)
  useEffect(() => {
    const handleStorageCleared = () => {
      const loginChecked = localStorage.getItem('checkedLogin');
      const storedToken = loginChecked
        ? localStorage.getItem('token')
        : sessionStorage.getItem('token');

      // Se o token sumiu do storage mas ainda existe no estado React, força logout
      if (!storedToken) {
        setUser(null);
        setToken(null);
        setPlan(null);
      }
    };

    // 'focus' cobre o caso de limpeza de cache feita nas configurações do browser
    window.addEventListener('focus', handleStorageCleared);
    // 'storage' cobre limpeza feita em outra aba (cross-tab logout)
    window.addEventListener('storage', handleStorageCleared);

    return () => {
      window.removeEventListener('focus', handleStorageCleared);
      window.removeEventListener('storage', handleStorageCleared);
    };
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      const loginChecked = localStorage.getItem('checkedLogin');
      let storedToken = loginChecked
        ? localStorage.getItem('token')
        : sessionStorage.getItem('token');

      // Nova aba sem token em sessionStorage (usuário não marcou "Manter
      // conectado") — tenta refresh via cookie HTTP-only (compartilhado entre
      // abas). Se o refresh funcionar, popula o token no storage atual e
      // segue. Isso evita o redirect pro /login quando o usuário abre um
      // vídeo em outra aba via "Abrir em uma nova aba".
      if (!storedToken) {
        try {
          const refreshResp = await authApi.post('/auth/refresh');
          const newToken = refreshResp?.data?.token;
          if (newToken) {
            const storage = loginChecked ? localStorage : sessionStorage;
            storage.setItem('token', newToken);
            storedToken = newToken;
          }
        } catch (_) {
          // Sem cookie de refresh válido — segue pro fluxo de não-autenticado
        }
      }

      if (storedToken) {
        setToken(storedToken);

        // Carrega dados do cache primeiro (se existirem) para evitar flash de conteúdo
        const storage = loginChecked ? localStorage : sessionStorage;
        const cachedUser = storage.getItem('user');
        const cachedPlan = storage.getItem('plano');

        if (cachedUser) {
          try {
            setUser(JSON.parse(cachedUser));
          } catch (e) {
            console.error('Error parsing cached user:', e);
          }
        }

        if (cachedPlan) {
          try {
            setPlan(JSON.parse(cachedPlan));
          } catch (e) {
            console.error('Error parsing cached plan:', e);
          }
        }

        // Busca dados atualizados do usuário via GET /me
        try {
          const response = await authService.getMe();
          if (response.data?.success && response.data?.user) {
            setUser(response.data.user);
            // Atualiza cache do usuário
            storage.setItem('user', JSON.stringify(response.data.user));
            // Regrava o userId: só era salvo no login(), some ao reidratar em nova aba
            storage.setItem('userId', response.data.user.id || response.data.user.id_user);

            // Sempre sincroniza o plano (limpa cache se backend devolver null,
            // ex.: subscription cancelada) — evita mostrar dados stale do trial.
            if (response.data.plano) {
              setPlan(response.data.plano);
              storage.setItem('plano', JSON.stringify(response.data.plano));
            } else {
              setPlan(null);
              storage.removeItem('plano');
            }
          } else {
            // Token inválido, limpa tudo
            clearAuth();
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          clearAuth();
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  const clearAuth = () => {
    setUser(null);
    setToken(null);
    setPlan(null);
    clearAuthStorage();
    const videoFeaturesStore = useVideoFeaturesStore.getState();
    videoFeaturesStore.resetFeatures();
    videoFeaturesStore.clearAllTempFiles();
  };

  const login = (userData: User, authToken: string, planData?: Plan, keepConnected: boolean = false) => {
    setUser(userData);
    setToken(authToken);
    if (planData) {
      setPlan(planData);
    }

    const storage = keepConnected ? localStorage : sessionStorage;

    // Save to appropriate storage (apenas token, dados vêm de GET /me)
    storage.setItem('token', authToken);
    storage.setItem('user', JSON.stringify(userData)); // Cache inicial
    storage.setItem('userId', userData.id || userData.id_user);

    if (planData) {
      storage.setItem('plano', JSON.stringify(planData));
    }

    if (keepConnected) {
      localStorage.setItem('checkedLogin', 'true');
    }
  };

  const logout = async () => {
    try {
      // Chama endpoint de logout no backend (limpa refresh token)
      await authService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Limpa estado local
      clearAuth();
    }
  };

  const isMember = user?.nivel === 'member';
  const isAdminMember = isMember && user?.access_type === 'ADMIN';

  const hasPermission = (featureKey: string, level: 'view' | 'edit' = 'view'): boolean => {
    if (!user) return false;
    if (user.nivel !== 'member') return true;
    if (isAdminMember) return true;
    const perms = user.permissions || [];
    const required = `feature:${featureKey}:${level}`;
    if (level === 'edit') return perms.includes(required);
    return perms.includes(`feature:${featureKey}:view`) || perms.includes(`feature:${featureKey}:edit`);
  };

  const value: AuthContextType = {
    user,
    plan,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    logout,
    refreshUser,
    hasPermission,
    isMember,
    isAdminMember,
  };

  return <CustomAuthContext.Provider value={value}>{children}</CustomAuthContext.Provider>;
};

export const useCustomAuth = (): AuthContextType => {
  useLanguage();
  const context = useContext(CustomAuthContext);
  if (!context) {
    throw new Error(translateInterfaceText("useCustomAuth must be used within CustomAuthProvider"));
  }
  return context;
};
